import React, { useEffect, useMemo, useState, useCallback } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { getStripe } from "./StripeCheckoutSheet";

// Renders the Stripe embedded checkout INLINE (fills its parent container),
// so it can live continuously inside another popup flow.
export function EmbeddedCheckoutInline({ clientSecret, sessionId, onPaid }) {
  const [stripe, setStripe] = useState(null);
  const [status, setStatus] = useState("checkout"); // checkout | confirming | paid

  useEffect(() => { getStripe().then(setStripe); setStatus("checkout"); }, []);

  const pollPaid = useCallback(async () => {
    setStatus("confirming");
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); setTimeout(() => onPaid && onPaid(), 1200); return; }
      } catch {}
      tries += 1;
      if (tries < 12) setTimeout(tick, 1500);
      else { onPaid && onPaid(); }
    };
    tick();
  }, [sessionId, onPaid]);

  const options = useMemo(() => ({ clientSecret, onComplete: pollPaid }), [clientSecret, pollPaid]);

  if (status === "paid") {
    return (
      <div className="grid place-items-center h-full text-center px-6" style={{ background: "#fff" }} data-testid="tc-checkout-paid">
        <div>
          <CheckCircle2 size={56} className="mx-auto" style={{ color: "#5B63E8" }} />
          <div className="font-serif-display text-3xl mt-4" style={{ color: "#171826" }}>Welcome to Legacy!</div>
          <p className="text-sm mt-2" style={{ color: "#565A6E" }}>Unlocking the full Time Capsule…</p>
        </div>
      </div>
    );
  }
  if (status === "confirming") {
    return (
      <div className="grid place-items-center h-full" style={{ background: "#fff" }}>
        <div className="text-center">
          <Loader2 size={40} className="animate-spin mx-auto" style={{ color: "#5B63E8" }} />
          <div className="mt-3 text-sm" style={{ color: "#565A6E" }}>Confirming your payment…</div>
        </div>
      </div>
    );
  }
  if (stripe && clientSecret) {
    return (
      <div className="rounded-xl overflow-hidden" style={{ background: "#fff" }} data-testid="tc-embedded-checkout">
        <EmbeddedCheckoutProvider stripe={stripe} options={options}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    );
  }
  return (
    <div className="grid place-items-center py-16" style={{ background: "#fff" }}>
      <Loader2 size={36} className="animate-spin" style={{ color: "#5B63E8" }} />
    </div>
  );
}
